import { ChevronDownIcon, DatabaseIcon, TableIcon, XIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@radix-ui/react-icons";
import { CurlyBracesIcon, LayersIcon } from "lucide-react";
import { useMemo } from "react";

import { BlockEnv, useBlockEnv } from "../env-context";
import FieldLabel from "../FieldLabel";
import { FieldType } from "../types";

import { useSourcesV2 } from "./sourcesV2Context";
import { SourceType, type SourceItem, type SourcesV2 } from "./sourceTypes";
export { SourceType } from "./sourceTypes";
export type { SourceColumnGroup, SourceItem, SourceSheetGroup, SourcesV2 } from "./sourceTypes";

import {
  DropdownMenu,
  DropdownMenuBasicTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchableDropdown } from "@/components/ui/searchable-dropdown";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";

type LegacySources = Record<SourceType, string[]>;

interface SelectSourceProps {
  name?: string;
  value?: string;
  /**
   * Legacy shape: map of source-type → flat list of string labels. Values
   * persisted to config equal the label (same-sheet sibling titles).
   */
  sources?: LegacySources;
  /**
   * Preferred shape: typed `SourceItem`s split into per-source-type lists
   * plus an optional `sheets` layer for composition navigation. When both
   * `sources` and `sourcesV2` are provided, `sourcesV2` wins.
   */
  sourcesV2?: SourcesV2;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  type?: SourceType;
  fieldType?: FieldType;
  forceOpen?: boolean;
}

const getSourceIcon = (sourceType: SourceType) => {
  switch (sourceType) {
    case SourceType.Column:
      return TableIcon;
    case SourceType.Dataset:
      return DatabaseIcon;
    case SourceType.InputVariable:
      return CurlyBracesIcon;
    default:
      return TableIcon;
  }
};

/**
 * Normalize legacy `Record<SourceType, string[]>` into the richer v2 shape
 * so the dropdown has a single render path. Each entry uses its label as
 * both the persisted value and the display text (legacy behavior).
 */
const legacyToV2 = (sources: LegacySources | undefined): SourcesV2 => {
  if (!sources) {
    return {};
  }
  const out: SourcesV2 = {};
  for (const [rawType, list] of Object.entries(sources)) {
    const sourceType = rawType as SourceType;
    out[sourceType] = list.map(label => ({
      id: `${sourceType}-${label}`,
      label,
      value: label,
    }));
  }
  return out;
};

interface DropdownItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  value?: string;
  sheetLabel?: string;
  tableLabel?: string;
  parentLabel?: string;
  searchKeywords?: string;
}

const getSourceDisplayLabel = (item?: Pick<DropdownItem, "label" | "parentLabel"> | null) => {
  if (!item) {
    return undefined;
  }
  if (item.parentLabel) {
    return `${item.parentLabel}.${item.label}`;
  }
  return item.label;
};

const buildSourceSearchKeywords = ({
  label,
  parentLabel,
  categoryLabel,
  sheetLabel,
  tableLabel,
}: {
  label: string;
  parentLabel?: string;
  categoryLabel?: string;
  sheetLabel?: string;
  tableLabel?: string;
}): string | undefined => {
  const keywords = new Set<string>();
  if (parentLabel) {
    keywords.add(parentLabel);
    keywords.add(`${parentLabel}.${label}`);
  }
  if (categoryLabel && categoryLabel !== label) {
    keywords.add(categoryLabel);
    keywords.add(`${categoryLabel}.${label}`);
  }
  if (sheetLabel) {
    keywords.add(sheetLabel);
  }
  if (tableLabel) {
    keywords.add(tableLabel);
  }
  if (keywords.size === 0) {
    return undefined;
  }
  return [...keywords].join(" ");
};

const SelectSource = ({
  name,
  value,
  sources,
  sourcesV2,
  onValueChange,
  disabled = false,
  containerClassName,
  className,
  placeholder,
  fieldType = FieldType.Default,
  forceOpen = false,
}: SelectSourceProps) => {
  const env = useBlockEnv();
  const ambientSourcesV2 = useSourcesV2();

  // Resolution order: explicit `sourcesV2` prop → ambient context value →
  // legacy `sources` record. The explicit prop wins so a per-block (e.g.
  // Composition) override can narrow the picker without fighting the
  // workspace-wide context installed by the sidebar.
  const normalized = useMemo<SourcesV2>(() => {
    if (sourcesV2) {
      return sourcesV2;
    }
    if (ambientSourcesV2) {
      // Merge legacy sources (sibling titles) with the ambient v2 shape so
      // Smart-Table blocks still see their own sibling columns alongside
      // the cross-sheet `sheets` layer.
      const merged: SourcesV2 = { ...ambientSourcesV2 };
      if (sources) {
        const legacy = legacyToV2(sources);
        for (const sourceType of Object.values(SourceType)) {
          const legacyItems = legacy[sourceType];
          if (!legacyItems || legacyItems.length === 0) {
            continue;
          }
          if (!merged[sourceType] || merged[sourceType]!.length === 0) {
            merged[sourceType] = legacyItems;
          }
        }
      }
      return merged;
    }
    return legacyToV2(sources);
  }, [ambientSourcesV2, sources, sourcesV2]);

  const { categories, valueToItem } = useMemo(() => {
    const cats: { label: string; items: DropdownItem[] }[] = [];
    const map = new Map<string, DropdownItem>();

    const addItems = (
      label: string,
      items: SourceItem[],
      icon: React.ComponentType<{ className?: string }>,
      isSheetGroup = false,
    ) => {
      if (items.length === 0) {
        return;
      }
      const mappedItems = items.map(item => {
        const entry = {
          id: item.id,
          label: item.label,
          description:
            isSheetGroup && item.tableLabel && item.sheetLabel
              ? `${item.tableLabel} / ${item.sheetLabel}`
              : undefined,
          icon,
          value: item.value,
          sheetLabel: item.sheetLabel,
          tableLabel: item.tableLabel,
          parentLabel: item.parentLabel,
          searchKeywords: buildSourceSearchKeywords({
            label: item.label,
            parentLabel: item.parentLabel,
            categoryLabel: label,
            sheetLabel: item.sheetLabel,
            tableLabel: item.tableLabel,
          }),
        };
        map.set(item.value, entry);
        return entry;
      });
      cats.push({ label, items: mappedItems });
    };

    for (const sourceType of Object.values(SourceType)) {
      addItems(sourceType, normalized[sourceType] ?? [], getSourceIcon(sourceType));
    }
    for (const group of normalized.columnGroups ?? []) {
      addItems(group.label, group.items, TableIcon);
    }
    for (const group of normalized.sheets ?? []) {
      addItems(group.label, group.items, LayersIcon, true);
    }
    return { categories: cats, valueToItem: map };
  }, [normalized]);

  const selectedItem = useMemo(() => {
    if (!value) {
      return undefined;
    }

    const hit = valueToItem.get(value);
    if (hit) {
      return hit;
    }

    // Value is present but no longer matches any discovered column. This
    // typically means the upstream column was deleted or moved out of the
    // workspace's accessible scope. Surface a clearly-muted placeholder so
    // the user can "clear" (via the removeable button) and rebind.
    return {
      id: `unavailable-${value}`,
      label: "Unavailable source",
      description: "The referenced column is no longer available",
      icon: TableIcon,
      value,
    };
  }, [value, valueToItem]);

  const emptyMessage = useMemo(() => {
    if (env === BlockEnv.AGENT) {
      return "No sources available. Add input variables to use them as sources.";
    }
    return "No sources available.";
  }, [env]);

  const handleSelect = (item: { value?: string; label?: string } | null) => {
    if (!item) {
      onValueChange("");
      return;
    }
    onValueChange(item.value ?? item.label ?? "");
  };

  const compositionTableTree = useMemo(() => {
    const tableMap = new Map<string, Map<string, DropdownItem[]>>();

    for (const group of normalized.sheets ?? []) {
      for (const item of group.items) {
        const tableLabel = item.tableLabel ?? "Other tables";
        const sheetLabel = item.sheetLabel ?? group.label;
        const mapped = valueToItem.get(item.value);

        if (!mapped) {
          continue;
        }

        if (!tableMap.has(tableLabel)) {
          tableMap.set(tableLabel, new Map<string, DropdownItem[]>());
        }
        const sheetMap = tableMap.get(tableLabel)!;
        if (!sheetMap.has(sheetLabel)) {
          sheetMap.set(sheetLabel, []);
        }
        sheetMap.get(sheetLabel)!.push(mapped);
      }
    }

    return [...tableMap.entries()]
      .map(([tableLabel, sheetMap]) => ({
        tableLabel,
        sheets: [...sheetMap.entries()]
          .map(([sheetLabel, items]) => ({
            sheetLabel,
            items: [...items].sort((a, b) => a.label.localeCompare(b.label)),
          }))
          .sort((a, b) => a.sheetLabel.localeCompare(b.sheetLabel)),
      }))
      .sort((a, b) => a.tableLabel.localeCompare(b.tableLabel));
  }, [normalized.sheets, valueToItem]);

  const hasMultipleCompositionTables = compositionTableTree.length > 1;

  const hasAnyItems = categories.some(category => category.items.length > 0);
  const hasCascadingCompositionMenu = hasMultipleCompositionTables;

  const trigger = (
    <button
      type="button"
      className={cn(
        "flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      disabled={disabled}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {selectedItem?.icon && (
          <selectedItem.icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        )}
        <OverflowDisplayTooltip
          key={getSourceDisplayLabel(selectedItem) ?? "placeholder"}
          content={getSourceDisplayLabel(selectedItem) ?? placeholder ?? "Select..."}
          className={cn(selectedItem?.label ? "" : "text-muted-foreground")}
          shouldShow={Boolean(selectedItem?.label)}
        >
          {getSourceDisplayLabel(selectedItem) || placeholder || "Select..."}
        </OverflowDisplayTooltip>
      </div>
      <div className="flex items-center gap-1">
        {selectedItem && !disabled && (
          <button
            type="button"
            className="inline-flex h-4 w-4 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onValueChange("");
            }}
            aria-label="Clear source"
          >
            <XIcon className="h-3 w-3" />
          </button>
        )}
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0 opacity-50" />
      </div>
    </button>
  );

  return (
    <div className={cn(containerClassName, name ? "flex items-center space-x-12" : "w-full")}>
      {name && <FieldLabel name={name} fieldType={fieldType} htmlFor={name} />}
      {hasCascadingCompositionMenu ? (
        <DropdownMenu>
          <DropdownMenuBasicTrigger asChild>{trigger}</DropdownMenuBasicTrigger>
          <DropdownMenuContent align="start" className="max-h-[360px] w-[360px] overflow-y-auto">
            {!hasAnyItems && (
              <DropdownMenuLabel className="text-muted-foreground">
                {emptyMessage}
              </DropdownMenuLabel>
            )}

            {categories
              .filter(category => !compositionTableTree.length || !category.label.includes("/"))
              .map((category, categoryIndex, visibleCategories) => (
                <div key={category.label}>
                  <DropdownMenuLabel>{category.label}</DropdownMenuLabel>
                  {category.items.map(item => (
                    <DropdownMenuItem
                      key={item.id}
                      onSelect={() => handleSelect(item)}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        {item.icon && (
                          <item.icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        )}
                        <OverflowDisplayTooltip content={item.label}>
                          {item.label}
                        </OverflowDisplayTooltip>
                      </div>
                      {selectedItem?.value === item.value && <CheckIcon className="h-4 w-4" />}
                    </DropdownMenuItem>
                  ))}
                  {(categoryIndex < visibleCategories.length - 1 ||
                    compositionTableTree.length > 0) && <DropdownMenuSeparator />}
                </div>
              ))}

            {compositionTableTree.length > 0 && (
              <>
                <DropdownMenuLabel>Tables</DropdownMenuLabel>
                {compositionTableTree.map(table => (
                  <DropdownMenuSub key={table.tableLabel}>
                    <DropdownMenuSubTrigger className="flex items-center gap-2">
                      <LayersIcon className="h-4 w-4 text-muted-foreground" />
                      <OverflowDisplayTooltip content={table.tableLabel}>
                        {table.tableLabel}
                      </OverflowDisplayTooltip>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-[280px]">
                      {table.sheets.map(sheet => (
                        <DropdownMenuSub key={`${table.tableLabel}-${sheet.sheetLabel}`}>
                          <DropdownMenuSubTrigger>{sheet.sheetLabel}</DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-[260px]">
                            {sheet.items.map(item => (
                              <DropdownMenuItem
                                key={item.id}
                                onSelect={() => handleSelect(item)}
                                className="flex items-center justify-between gap-2"
                              >
                                <OverflowDisplayTooltip content={item.label}>
                                  {item.label}
                                </OverflowDisplayTooltip>
                                {selectedItem?.value === item.value && (
                                  <CheckIcon className="h-4 w-4" />
                                )}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <SearchableDropdown
          categories={categories}
          selectedItem={selectedItem}
          emptyMessage={emptyMessage}
          placeholder={placeholder}
          removeable
          readonly={disabled}
          onSelect={handleSelect}
          className={cn(!name && "w-full")}
          triggerClassName={cn(className, "bg-white")}
          forceOpen={forceOpen}
        />
      )}
    </div>
  );
};

export default SelectSource;
